/**
 * Profile — Phase 2 (Executive Redesign, Entry Experience).
 *
 * Post-registration 3-step onboarding wizard. Presentation migrated to
 * `@/design-system` primitives + `--ds-*` tokens.
 *
 * MATERIAL-SCOPE PRESERVED VERBATIM:
 *   • Wizard step progression + step-1/step-2/step-3 validation rules
 *   • `persist()` diff detection + payload shape sent to updateProfile
 *   • NUMERIC coercion set (time_in_business_months, monthly_revenue, …)
 *   • Credit score bounds [300, 850] + `estimated_credit_score_unknown`
 *   • Payment history override + `/profile/payment-history/clear-override`
 *   • `refreshMe` + latest_readiness_id soft-poll + toast on completion
 *   • Every `data-testid` (profile-*)
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Home, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthContext";
import { api, formatApiError } from "@/api/client";
import AuthShell from "./AuthShell";
import { Button, Alert, Badge } from "@/design-system";

// Mirrors the backend constants. Kept in sync manually.
const INDUSTRIES = [
  "Accommodation & Hospitality", "Agriculture & Farming", "Automotive & Repair",
  "Beauty & Personal Care", "Construction & Contracting",
  "Consulting & Professional Services", "Education & Training",
  "Energy & Utilities", "Entertainment & Media", "Financial Services",
  "Food & Beverage / Restaurants", "Freight, Logistics & Transportation",
  "Health & Wellness", "Home Services", "Information Technology & Software",
  "Legal Services", "Manufacturing", "Marketing & Advertising",
  "Nonprofit & Community", "Real Estate", "Retail & E-commerce",
  "Trades (Plumbing, Electrical, HVAC)", "Veterinary & Pet Care",
  "Wholesale & Distribution", "Other",
];

const FUNDING_PURPOSES = [
  "Working capital", "Expansion / new location", "Equipment purchase",
  "Inventory", "Marketing & growth", "Debt refinancing", "Hiring / payroll",
  "Real estate", "Other",
];

const STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","DC","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD",
  "MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD",
  "TN","TX","UT","VT","VA","WA","WV","WI","WY",
];

const STRUCTURES = [
  { value: "sole_proprietor", label: "Sole proprietor" },
  { value: "llc", label: "LLC" },
  { value: "corporation", label: "Corporation" },
  { value: "nonprofit", label: "Nonprofit" },
  { value: "other", label: "Other" },
];

export default function Profile() {
  const { user, updateProfile, refresh, refreshMe } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const initialForm = useMemo(
    () => ({
      first_name: user?.profile?.first_name || "",
      last_name: user?.profile?.last_name || "",
      phone: user?.profile?.phone || "",
      state: user?.profile?.state || "",
      is_business_owner: user?.profile?.is_business_owner ?? null,
      has_existing_business: user?.profile?.has_existing_business ?? null,
      business_name: user?.profile?.business_name || "",
      business_structure: user?.profile?.business_structure || "",
      has_ein: user?.profile?.has_ein ?? null,
      has_business_bank_account: user?.profile?.has_business_bank_account ?? null,
      industry: user?.profile?.industry || "",
      time_in_business_months: user?.profile?.time_in_business_months ?? "",
      monthly_revenue: user?.profile?.monthly_revenue ?? "",
      annual_revenue: user?.profile?.annual_revenue ?? "",
      has_existing_business_funding: user?.profile?.has_existing_business_funding ?? null,
      estimated_credit_score: user?.profile?.estimated_credit_score ?? 680,
      estimated_credit_score_unknown: user?.profile?.estimated_credit_score_unknown ?? false,
      funding_goal_amount: user?.profile?.funding_goal_amount ?? "",
      funding_purpose: user?.profile?.funding_purpose || "",
      funding_purpose_notes: user?.profile?.funding_purpose_notes || "",
      self_reported_payment_history: user?.profile?.self_reported_payment_history ?? null,
      effective_payment_history: user?.profile?.effective_payment_history ?? null,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user?.id]
  );
  const dbSnapshot = useMemo(
    () => ({
      ...initialForm,
      estimated_credit_score: user?.profile?.estimated_credit_score ?? null,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user?.id]
  );
  const [form, setForm] = useState(initialForm);
  const initialRef = useRef(dbSnapshot);

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function set(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  async function persist(extra = {}) {
    const payload = { ...form, ...extra };
    const NUMERIC = new Set([
      "time_in_business_months",
      "monthly_revenue",
      "annual_revenue",
      "estimated_credit_score",
      "funding_goal_amount",
    ]);
    if (payload.estimated_credit_score_unknown) {
      payload.estimated_credit_score = null;
    }
    const diff = {};
    const initial = initialRef.current;
    for (const key of Object.keys(payload)) {
      let v = payload[key];
      if (typeof v === "string") v = v.trim();
      if (v === "") v = null;
      if (NUMERIC.has(key) && v !== null && v !== undefined) {
        const n = Number(v);
        v = Number.isFinite(n) ? n : null;
      }
      let initVal = initial[key];
      if (typeof initVal === "string") initVal = initVal.trim();
      if (initVal === "") initVal = null;
      if (NUMERIC.has(key) && initVal !== null && initVal !== undefined) {
        const n = Number(initVal);
        initVal = Number.isFinite(n) ? n : null;
      }
      if (v !== initVal) diff[key] = v;
    }
    if (Object.keys(diff).length === 0) {
      return user;
    }
    setSaving(true);
    try {
      const updated = await updateProfile(diff);
      initialRef.current = { ...initial, ...diff };
      return updated;
    } catch (e) {
      setError(formatApiError(e));
      throw e;
    } finally {
      setSaving(false);
    }
  }

  async function next() {
    setError("");
    if (step === 1) {
      if (!form.first_name || !form.last_name || !form.state) {
        setError("Please add your first name, last name and state before continuing.");
        return;
      }
    } else if (step === 2) {
      if (form.is_business_owner === null) {
        setError("Let us know whether you own a business.");
        return;
      }
      if (form.is_business_owner) {
        const need = [
          ["has_existing_business", "Do you currently operate a business?"],
          ["business_name", "Business name"],
          ["business_structure", "Business structure"],
          ["has_ein", "Do you have an EIN?"],
          ["has_business_bank_account", "Do you have a business bank account?"],
          ["industry", "Industry"],
          ["time_in_business_months", "Time in business (months)"],
          ["monthly_revenue", "Monthly revenue"],
          ["annual_revenue", "Annual revenue"],
          ["has_existing_business_funding", "Existing business funding?"],
        ];
        for (const [k, label] of need) {
          if (form[k] === null || form[k] === "" || form[k] === undefined) {
            setError(`Please complete: ${label}.`);
            return;
          }
        }
      }
    }
    try {
      await persist();
      setStep((s) => Math.min(3, s + 1));
    } catch {
      /* error already set */
    }
  }

  async function complete() {
    setError("");
    if (!form.funding_goal_amount || Number(form.funding_goal_amount) <= 0) {
      setError("Please enter a funding goal amount greater than $0.");
      return;
    }
    if (!form.funding_purpose) {
      setError("Please select a funding purpose.");
      return;
    }
    if (!form.estimated_credit_score_unknown && (!form.estimated_credit_score || form.estimated_credit_score < 300 || form.estimated_credit_score > 850)) {
      setError("Please set a credit score between 300 and 850, or check 'I don't know'.");
      return;
    }
    try {
      const updated = await persist();
      const me = await refreshMe();
      void updated;
      if (me?.funding_profile_complete && !me?.latest_readiness_id) {
        await new Promise((r) => setTimeout(r, 700));
        await refreshMe();
      }
      toast.success("Funding profile complete. Your readiness score is ready.");
      navigate("/dashboard", { replace: true });
    } catch {
      /* noop */
    }
  }

  return (
    <AuthShell
      testId="profile-page"
      wide
      title={step === 1 ? "Continue with your profile." : step === 2 ? "Your business." : "Funding goals."}
      subtitle={
        step === 1
          ? "Step 1 of 3 — the basics so we can personalize your readiness assessment."
          : step === 2
          ? "Step 2 of 3 — this helps us match you to the right partners later."
          : "Step 3 of 3 — what funding are you working toward?"
      }
    >
      <Stepper step={step} />

      <EmailVerificationChip user={user} />

      {(user?.funding_profile_complete || user?.mortgage_intake) && (
        <MortgageReadinessCard user={user} />
      )}

      <div className="mt-8 space-y-5">
        {step === 1 && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Text label="First name" testId="profile-first-name" value={form.first_name} onChange={(v) => set("first_name", v)} />
              <Text label="Last name" testId="profile-last-name" value={form.last_name} onChange={(v) => set("last_name", v)} />
            </div>
            <Text label="Phone" testId="profile-phone" value={form.phone} onChange={(v) => set("phone", v)} placeholder="(555) 123-4567" />
            <SelectField label="State" testId="profile-state" value={form.state} onChange={(v) => set("state", v)} options={STATES.map((s) => ({ value: s, label: s }))} />
          </>
        )}

        {step === 2 && (
          <>
            <YesNo
              label="Do you own a business (or plan to start one soon)?"
              testId="profile-is-business-owner"
              value={form.is_business_owner}
              onChange={(v) => set("is_business_owner", v)}
            />
            {form.is_business_owner === true && (
              <>
                <YesNo label="Do you have an existing business operating today?" testId="profile-has-existing-business" value={form.has_existing_business} onChange={(v) => set("has_existing_business", v)} />
                <Text label="Business name" testId="profile-business-name" value={form.business_name} onChange={(v) => set("business_name", v)} />
                <SelectField label="Business structure" testId="profile-business-structure" value={form.business_structure} onChange={(v) => set("business_structure", v)} options={STRUCTURES} />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <YesNo label="EIN obtained?" testId="profile-has-ein" value={form.has_ein} onChange={(v) => set("has_ein", v)} compact />
                  <YesNo label="Business bank account?" testId="profile-has-business-bank-account" value={form.has_business_bank_account} onChange={(v) => set("has_business_bank_account", v)} compact />
                </div>
                <SelectField label="Industry" testId="profile-industry" value={form.industry} onChange={(v) => set("industry", v)} options={INDUSTRIES.map((i) => ({ value: i, label: i }))} />
                <TimeInBusinessField
                  totalMonths={form.time_in_business_months}
                  onChange={(months) => set("time_in_business_months", months)}
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <NumberField label="Monthly revenue (USD)" testId="profile-monthly-revenue" value={form.monthly_revenue} onChange={(v) => set("monthly_revenue", v)} min={0} prefix="$" />
                  <NumberField label="Annual revenue (USD)" testId="profile-annual-revenue" value={form.annual_revenue} onChange={(v) => set("annual_revenue", v)} min={0} prefix="$" />
                </div>
                <YesNo label="Do you currently have business funding from another lender?" testId="profile-has-existing-business-funding" value={form.has_existing_business_funding} onChange={(v) => set("has_existing_business_funding", v)} />
              </>
            )}
          </>
        )}

        {step === 3 && (
          <>
            <CreditScoreSlider
              value={form.estimated_credit_score}
              unknown={form.estimated_credit_score_unknown}
              onValue={(v) => set("estimated_credit_score", v)}
              onUnknown={(v) => set("estimated_credit_score_unknown", v)}
            />
            <NumberField label="Funding goal amount (USD)" testId="profile-funding-goal-amount" value={form.funding_goal_amount} onChange={(v) => set("funding_goal_amount", v)} min={0} prefix="$" />
            <SelectField label="Primary funding purpose" testId="profile-funding-purpose" value={form.funding_purpose} onChange={(v) => set("funding_purpose", v)} options={FUNDING_PURPOSES.map((p) => ({ value: p, label: p }))} />
            <PaymentHistoryRadio
              value={form.self_reported_payment_history}
              effective={form.effective_payment_history}
              source={user?.payment_history_source}
              onChange={(v) => set("self_reported_payment_history", v)}
            />
            <Text label="Notes (optional)" testId="profile-funding-purpose-notes" value={form.funding_purpose_notes} onChange={(v) => set("funding_purpose_notes", v)} placeholder="Anything else we should know about your plans" />
          </>
        )}

        {error && (
          <Alert tone="danger" data-testid="profile-form-error">
            {error}
          </Alert>
        )}

        <div className="flex items-center justify-between pt-2">
          {step > 1 ? (
            <button
              type="button"
              data-testid="profile-back"
              onClick={() => setStep((s) => Math.max(1, s - 1))}
              className="inline-flex items-center gap-2 text-[13px] text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-accent)] transition-colors"
            >
              <ArrowLeft className="w-4 h-4" aria-hidden /> Back
            </button>
          ) : (
            <span />
          )}

          {step < 3 ? (
            <Button
              type="button"
              data-testid="profile-next"
              onClick={next}
              disabled={saving}
              loading={saving}
              size="md"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden /> : null}
              {saving ? "Saving…" : "Continue"} <ArrowRight className="w-4 h-4" aria-hidden />
            </Button>
          ) : (
            <Button
              type="button"
              data-testid="profile-complete"
              onClick={complete}
              disabled={saving}
              loading={saving}
              size="md"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden /> : <Check className="w-4 h-4" aria-hidden />}
              {saving ? "Completing…" : "Complete profile"}
            </Button>
          )}
        </div>
      </div>
    </AuthShell>
  );
}

function Stepper({ step }) {
  return (
    <div data-testid="profile-stepper" className="flex items-center gap-2">
      {[1, 2, 3].map((i) => {
        const done = step > i;
        const current = step >= i;
        return (
          <div key={i} className="flex-1 flex items-center gap-2">
            <div
              className={`w-7 h-7 rounded-[var(--ds-radius-pill)] text-[13px] font-semibold flex items-center justify-center border transition-colors ${
                current
                  ? "bg-[var(--ds-color-accent)] text-[var(--ds-color-accent-ink)] border-[var(--ds-color-accent)]"
                  : "bg-[var(--ds-color-surface)] text-[var(--ds-color-ink-muted)] border-[var(--ds-color-border)]"
              }`}
            >
              {done ? <Check className="w-3.5 h-3.5" aria-hidden /> : i}
            </div>
            {i < 3 && (
              <div
                className={`flex-1 h-px transition-colors ${
                  done ? "bg-[var(--ds-color-accent)]" : "bg-[var(--ds-color-border)]"
                }`}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

function FieldShell({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--ds-color-ink-muted)] mb-1.5">
        {label}
      </span>
      <div className="flex items-center gap-2 px-3 h-10 rounded-[var(--ds-radius-md)] bg-[var(--ds-color-surface)] border border-[var(--ds-color-border-strong)] focus-within:border-[var(--ds-color-accent)] transition-colors">
        {children}
      </div>
    </label>
  );
}

function Text({ label, testId, value, onChange, placeholder }) {
  return (
    <FieldShell label={label}>
      <input
        data-testid={testId}
        type="text"
        value={value || ""}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="flex-1 bg-transparent outline-none text-[14px] text-[var(--ds-color-ink)] placeholder:text-[var(--ds-color-ink-subtle)]"
      />
    </FieldShell>
  );
}

function NumberField({ label, testId, value, onChange, min, prefix }) {
  return (
    <FieldShell label={label}>
      {prefix && <span className="text-[13px] text-[var(--ds-color-ink-muted)]">{prefix}</span>}
      <input
        data-testid={testId}
        type="number"
        value={value ?? ""}
        min={min}
        onChange={(e) => onChange(e.target.value)}
        className="flex-1 bg-transparent outline-none text-[14px] text-[var(--ds-color-ink)] placeholder:text-[var(--ds-color-ink-subtle)]"
      />
    </FieldShell>
  );
}

function TimeInBusinessField({ totalMonths, onChange }) {
  const safeTotal = Number.isFinite(Number(totalMonths)) ? Math.max(0, parseInt(totalMonths, 10) || 0) : 0;
  const years = Math.floor(safeTotal / 12);
  const months = safeTotal % 12;
  function update(nextYears, nextMonths) {
    const y = Math.max(0, parseInt(nextYears, 10) || 0);
    const m = Math.max(0, Math.min(11, parseInt(nextMonths, 10) || 0));
    onChange(y * 12 + m);
  }
  return (
    <div>
      <label htmlFor="profile-time-in-business-years" className="block text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--ds-color-ink-muted)] mb-1.5">
        Time in business
      </label>
      <div className="grid grid-cols-2 gap-3">
        <div className="flex items-center gap-2 rounded-[var(--ds-radius-md)] border border-[var(--ds-color-border-strong)] bg-[var(--ds-color-surface)] px-3 h-10 focus-within:border-[var(--ds-color-accent)] transition-colors">
          <input
            data-testid="profile-time-in-business-years"
            type="number"
            min={0}
            max={80}
            value={years}
            onChange={(e) => update(e.target.value, months)}
            className="flex-1 bg-transparent outline-none text-[14px] text-[var(--ds-color-ink)]"
          />
          <span className="text-[11px] uppercase tracking-[0.12em] text-[var(--ds-color-ink-muted)]">Years</span>
        </div>
        <div className="flex items-center gap-2 rounded-[var(--ds-radius-md)] border border-[var(--ds-color-border-strong)] bg-[var(--ds-color-surface)] px-3 h-10 focus-within:border-[var(--ds-color-accent)] transition-colors">
          <input
            data-testid="profile-time-in-business-months"
            type="number"
            min={0}
            max={11}
            value={months}
            onChange={(e) => update(years, e.target.value)}
            className="flex-1 bg-transparent outline-none text-[14px] text-[var(--ds-color-ink)]"
          />
          <span className="text-[11px] uppercase tracking-[0.12em] text-[var(--ds-color-ink-muted)]">Months</span>
        </div>
      </div>
      <p className="text-[13px] text-[var(--ds-color-ink-subtle)] mt-1.5">
        Used to determine eligibility for funding partners.
      </p>
    </div>
  );
}

function SelectField({ label, testId, value, onChange, options }) {
  return (
    <FieldShell label={label}>
      <select
        data-testid={testId}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className={`flex-1 bg-transparent outline-none text-[14px] ${
          value ? "text-[var(--ds-color-ink)]" : "text-[var(--ds-color-ink-subtle)]"
        }`}
      >
        <option value="" disabled>Select…</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </FieldShell>
  );
}

function YesNo({ label, testId, value, onChange, compact }) {
  const Btn = ({ on, val, dataId }) => (
    <button
      type="button"
      data-testid={dataId}
      onClick={() => onChange(val)}
      className={`flex-1 px-4 py-2.5 rounded-[var(--ds-radius-md)] text-[13px] font-medium border transition-colors ${
        on
          ? "bg-[var(--ds-color-accent-soft)] text-[var(--ds-color-ink)] border-[var(--ds-color-accent)]"
          : "bg-[var(--ds-color-surface)] text-[var(--ds-color-ink-muted)] border-[var(--ds-color-border)] hover:border-[var(--ds-color-border-strong)]"
      }`}
    >
      {val ? "Yes" : "No"}
    </button>
  );
  return (
    <div data-testid={testId}>
      <div className={`text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--ds-color-ink-muted)] ${compact ? "mb-2" : "mb-2.5"}`}>
        {label}
      </div>
      <div className="flex gap-2">
        <Btn on={value === true} val={true} dataId={`${testId}-yes`} />
        <Btn on={value === false} val={false} dataId={`${testId}-no`} />
      </div>
    </div>
  );
}

function PaymentHistoryRadio({ value, onChange, effective, source }) {
  const options = [
    { value: "none_48_months", label: "No late payments in the last 48 months." },
    { value: "older_than_48_months", label: "Late payments older than 48 months." },
    { value: "one_or_two_recent", label: "One or two recent late payments." },
    { value: "multiple_recent", label: "Multiple recent late payments." },
    { value: "dont_know", label: "I'm not sure." },
  ];
  const verified = source === "verified" && !!effective;
  const [clearing, setClearing] = useState(false);

  async function handleClearOverride() {
    if (!window.confirm("Clear the verified value and re-enable self-assessment editing? Your next credit report will overwrite this again.")) return;
    setClearing(true);
    try {
      await api.post("/profile/payment-history/clear-override");
      toast.success("Verified value cleared. You can now edit your self-assessment.");
      window.location.reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setClearing(false);
    }
  }

  const displayLabel = verified
    ? (options.find((o) => o.value === effective)?.label || "Detected payment history")
    : null;

  return (
    <div data-testid="profile-payment-history">
      <div className="flex items-center gap-2 mb-3">
        <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--ds-color-ink-muted)]">
          Self-reported payment history
        </div>
        {verified && (
          <span
            data-testid="profile-payment-history-verified-pill"
            className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.14em] px-2 py-0.5 rounded-[var(--ds-radius-pill)] bg-[var(--ds-color-accent-soft)] text-[var(--ds-color-accent)] border border-[var(--ds-color-accent)]"
          >
            <ShieldCheckIcon /> Verified from credit report
          </span>
        )}
      </div>
      {verified ? (
        <div
          data-testid="profile-payment-history-locked"
          className="rounded-[var(--ds-radius-md)] px-4 py-4 text-[13px] bg-[var(--ds-color-accent-soft)] border border-[var(--ds-color-accent)]"
        >
          <p className="text-[var(--ds-color-ink)] mb-2">
            <strong className="text-[var(--ds-color-accent)]">{displayLabel}</strong>
          </p>
          <p className="text-[13px] leading-relaxed mb-3 text-[var(--ds-color-ink-muted)]">
            This value was derived from your most recent credit report. Upload a new report to
            refresh it, or override with a self-assessment below if you want the readiness engine
            to use your own answer instead.
          </p>
          <button
            type="button"
            data-testid="profile-payment-history-clear-override"
            onClick={handleClearOverride}
            disabled={clearing}
            className="text-[13px] underline text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)] transition-colors disabled:opacity-50"
          >
            {clearing ? "Clearing…" : "Override with self-assessment"}
          </button>
        </div>
      ) : (
        <>
          <p className="text-[13px] mb-3 leading-relaxed text-[var(--ds-color-ink-muted)]">
            Pick the option that best describes your recent payment behaviour. When you upload
            a credit report, the detected data will override this.
          </p>
          <div className="space-y-2">
            {options.map((opt) => {
              const selected = value === opt.value;
              const testId = `profile-payment-history-${opt.value ?? "unsure"}`;
              return (
                <button
                  key={opt.value ?? "unsure"}
                  type="button"
                  data-testid={testId}
                  onClick={() => onChange(opt.value)}
                  className={`w-full text-left px-4 py-3 rounded-[var(--ds-radius-md)] text-[13px] transition-colors border ${
                    selected
                      ? "bg-[var(--ds-color-accent-soft)] text-[var(--ds-color-ink)] border-[var(--ds-color-accent)]"
                      : "bg-[var(--ds-color-surface)] text-[var(--ds-color-ink-muted)] border-[var(--ds-color-border)] hover:border-[var(--ds-color-border-strong)]"
                  }`}
                >
                  <span className="inline-flex items-center gap-2.5">
                    <span
                      className={`w-3.5 h-3.5 rounded-[var(--ds-radius-pill)] inline-block ${
                        selected
                          ? "border-4 border-[var(--ds-color-accent)] bg-[var(--ds-color-background)]"
                          : "border-2 border-[var(--ds-color-ink-muted)]"
                      }`}
                    />
                    {opt.label}
                  </span>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function ShieldCheckIcon() {
  return (
    <svg viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  );
}

function CreditScoreSlider({ value, unknown, onValue, onUnknown }) {
  const v = unknown ? null : (value ?? 680);
  return (
    <div>
      <div className="flex items-baseline justify-between mb-3">
        <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--ds-color-ink-muted)]">
          Estimated credit score
        </span>
        <span
          className={`text-[22px] font-semibold ${
            unknown ? "text-[var(--ds-color-ink-muted)]" : "text-[var(--ds-color-accent)]"
          }`}
          data-testid="profile-credit-score-value"
          style={{ fontFamily: "var(--ds-font-display)" }}
        >
          {unknown ? "—" : v}
        </span>
      </div>
      <input
        data-testid="profile-credit-score-slider"
        type="range"
        min={300}
        max={850}
        step={5}
        value={v ?? 680}
        disabled={unknown}
        onChange={(e) => onValue(Number(e.target.value))}
        className="w-full"
        style={{ accentColor: "var(--ds-color-accent)", opacity: unknown ? 0.4 : 1 }}
      />
      <div className="flex items-center justify-between text-[11px] text-[var(--ds-color-ink-subtle)] mt-1">
        <span>300</span><span>850</span>
      </div>
      <label className="mt-3 flex items-center gap-2 text-[13px] text-[var(--ds-color-ink-muted)] cursor-pointer">
        <input
          data-testid="profile-credit-score-unknown"
          type="checkbox"
          checked={unknown}
          onChange={(e) => onUnknown(e.target.checked)}
          style={{ accentColor: "var(--ds-color-accent)" }}
        />
        I don't know my credit score
      </label>
    </div>
  );
}

/* -------------------------------------------------------------------------
 * Email verification affordance (Wave 2 executive redesign).
 *
 * Non-intrusive replacement for the removed global `<UnverifiedBanner />`.
 * Renders a compact status row above the profile wizard so the user can
 * see whether their email is verified and re-send the verification link
 * without a full-bleed amber banner sitting on top of every page.
 *
 * Test IDs:
 *   profile-email-verification-chip         — root wrapper
 *   profile-email-verification-status       — status badge
 *   profile-email-verification-resend       — resend link button
 * ---------------------------------------------------------------------- */

function EmailVerificationChip({ user }) {
  const verified = !!user?.email_verified;
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  async function resend() {
    setBusy(true);
    try {
      await api.post("/auth/resend-verification");
      setSent(true);
      toast.success("Verification email sent. Check your inbox.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      data-testid="profile-email-verification-chip"
      className="mt-6 flex flex-wrap items-center gap-3 rounded-[var(--ds-radius-md)] border border-[var(--ds-color-border)] bg-[var(--ds-color-surface)] px-4 py-3"
    >
      <Mail
        className="h-4 w-4 flex-shrink-0 text-[var(--ds-color-ink-muted)]"
        aria-hidden
      />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--ds-color-ink-muted)]">
            Email verification
          </span>
          <Badge
            tone={verified ? "success" : "warning"}
            data-testid="profile-email-verification-status"
          >
            {verified ? "Verified" : "Pending"}
          </Badge>
        </div>
        <div className="mt-1 text-[13px] leading-snug text-[var(--ds-color-ink-muted)] truncate">
          {user?.email || "—"}
        </div>
      </div>
      {!verified && !sent && (
        <Button
          variant="secondary"
          size="sm"
          onClick={resend}
          loading={busy}
          disabled={busy}
          data-testid="profile-email-verification-resend"
        >
          {busy ? "Sending…" : "Resend link"}
        </Button>
      )}
      {sent && (
        <span
          data-testid="profile-email-verification-sent"
          className="text-[13px] font-medium text-[var(--ds-color-success)]"
        >
          Sent — check your inbox.
        </span>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------
 * Mortgage Readiness card (V2.0 Phase 4a → Phase 2 executive redesign)
 * ---------------------------------------------------------------------- */

function MortgageReadinessCard({ user }) {
  const hasIntake = !!user?.mortgage_intake;
  const label = hasIntake ? "Update mortgage intake" : "Complete mortgage intake";
  return (
    <div
      data-testid="profile-mortgage-readiness-card"
      className="mt-6 rounded-[var(--ds-radius-lg)] bg-[var(--ds-color-accent-soft)] border border-[var(--ds-color-accent)] p-5 flex items-start gap-4 flex-wrap"
    >
      <div className="w-11 h-11 rounded-[var(--ds-radius-md)] flex items-center justify-center flex-shrink-0 bg-[var(--ds-color-surface)] text-[var(--ds-color-accent)]">
        <Home className="w-5 h-5" aria-hidden />
      </div>
      <div className="flex-1 min-w-[220px]">
        <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--ds-color-accent)] mb-1">
          Mortgage readiness
        </div>
        <div
          className="text-[18px] leading-tight font-semibold text-[var(--ds-color-ink)]"
          style={{ fontFamily: "var(--ds-font-display)" }}
        >
          {hasIntake ? "Keep your mortgage inputs up to date." : "Ready to plan for a home purchase?"}
        </div>
        <p className="text-[13px] mt-1.5 leading-relaxed text-[var(--ds-color-ink-muted)]">
          Add income, debt, savings and target home details to see your Mortgage Readiness
          score, estimated purchase power and eligible loan programs.
        </p>
      </div>
      <Link
        to="/mortgage-intake"
        data-testid="profile-mortgage-readiness-cta"
        className="inline-flex items-center gap-1.5 rounded-[var(--ds-radius-md)] px-4 py-2 text-[13px] font-semibold flex-shrink-0 bg-[var(--ds-color-accent)] text-[var(--ds-color-accent-ink)] hover:bg-[var(--ds-color-accent-strong)] transition-colors"
      >
        {label} <ArrowRight className="w-3.5 h-3.5" aria-hidden />
      </Link>
    </div>
  );
}
